import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Overline } from "./anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Your name", type: "text", required: true },
  { name: "email", label: "Work email", type: "email", required: true },
  { name: "company", label: "Organisation", type: "text", required: false },
];

const INTERESTS = ["The Corporate Mask We Wear", "Relationships and Trust", "Authentic Leadership", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", team_size: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", team_size: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-ink py-24 md:py-36 border-t border-line-d grain" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-10 md:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <Overline>Start the conversation</Overline>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-6xl text-sand tracking-tight leading-[1.05]">
              Let's build a braver team, together.
            </h2>
            <p className="mt-8 text-muted-d text-base md:text-lg leading-relaxed max-w-md">
              Tell us a little about your organisation and what you're hoping to shift. We'll set up a no-pressure
              30 minute conversation to explore whether we're the right fit.
            </p>
            <div className="mt-10 space-y-3">
              <a href="mailto:tal@beyondthemask.com.au" className="group flex items-center gap-3 text-sand hover:text-terra transition-colors duration-300" data-testid="contact-email-link">
                <Mail size={18} className="text-terra" />
                <span className="text-base md:text-lg">tal@beyondthemask.com.au</span>
              </a>
              <a href="tel:+61430095792" className="group flex items-center gap-3 text-sand hover:text-terra transition-colors duration-300" data-testid="contact-phone-link">
                <Phone size={18} className="text-terra" />
                <span className="text-base md:text-lg">0430 095 792</span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="col-span-12 lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-terra p-12 flex flex-col items-start gap-5"
              data-testid="contact-success"
            >
              <div className="h-12 w-12 flex items-center justify-center bg-terra text-ink"><Check size={22} /></div>
              <h3 className="font-display text-3xl text-sand">Your message is on its way.</h3>
              <p className="text-muted-d max-w-md">We read every enquiry personally and will reply within two working days.</p>
              <button onClick={() => setDone(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-terra hover:text-sand transition-colors" data-testid="contact-reset">
                Send another →
              </button>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-8">
                  {FIELDS.map((f) => (
                    <div key={f.name} className={f.name === "company" ? "md:col-span-2" : ""}>
                      <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-d mb-3">{f.label}{f.required && " *"}</label>
                      <input
                        type={f.type}
                        value={form[f.name]}
                        onChange={(e) => update(f.name, e.target.value)}
                        className="w-full bg-transparent border-b border-line-d py-3 text-sand text-lg outline-none focus:border-terra transition-colors duration-300 placeholder:text-muted-d/40"
                        data-testid={`contact-${f.name}`}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-d mb-3">Team size</label>
                  <div className="flex flex-wrap gap-2">
                    {["1–20", "20–100", "100–500", "500+"].map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => update("team_size", s)}
                        className={`px-4 py-2 font-mono text-[11px] tracking-wide border transition-colors duration-300 ${
                          form.team_size === s ? "bg-sand text-ink border-sand" : "border-line-d text-muted-d hover:border-sand hover:text-sand"
                        }`}
                        data-testid={`contact-teamsize-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-d mb-3">I'm interested in</label>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => update("interest", s)}
                        className={`px-4 py-2 font-mono text-[11px] tracking-wide border transition-colors duration-300 ${
                          form.interest === s ? "bg-terra text-ink border-terra" : "border-line-d text-muted-d hover:border-sand hover:text-sand"
                        }`}
                        data-testid={`contact-interest-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-muted-d mb-3">What would you like to shift? *</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="w-full bg-transparent border-b border-line-d py-3 text-sand text-lg outline-none focus:border-terra transition-colors duration-300 resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-3 bg-sand text-ink py-5 font-mono text-xs tracking-[0.25em] uppercase hover:bg-terra transition-colors duration-300 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending…" : "Book a 30 minute conversation"}
                  {!loading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
